import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Select } from '../../../../components/inputs/Select';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { applicantsApi } from '../applicantsApi';
import {
  PICKABLE_APPLICANT_STATUS_OPTIONS,
  type ApplicantListItem,
  type ApplicantStatusValue,
} from '../applicantTypes';

/**
 * Moves an application to a stage — opened from the profile banner. Its own form and request, so an
 * edit to an email can never move a stage by accident.
 *
 * **Only the stages AFTER screening are offered** (19 Oct 2026): Received, Qualified and Not
 * Qualified belong to Initial Screening, which sets them. The banner offers this form only for an
 * applicant screened Qualified, and the server refuses anyone else. Among the later stages any may
 * follow any other: they were given as a vocabulary, not a workflow.
 */
export function ApplicantStatusModal({
  applicant,
  onClose,
  onSaved,
}: {
  applicant: ApplicantListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  // Seeded with the current stage where it is one the picker offers, so the form opens on what is
  // true; a freshly Qualified applicant opens EMPTY rather than on a stage nobody chose.
  const [status, setStatus] = useState<ApplicantStatusValue | null>(
    PICKABLE_APPLICANT_STATUS_OPTIONS.some((o) => o.value === applicant.status) ? applicant.status : null,
  );
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const changed = status !== null && status !== applicant.status;
  const canSave = changed && confirmed && !saving;

  async function save() {
    if (!canSave || status === null) return;

    setSaving(true);
    setError(null);

    try {
      const saved = await applicantsApi.setStatus(applicant.id, status);
      toast.success(`Moved to ${saved.statusLabel}.`);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not move the application.', 0);
      setError(failure);
      focusValidationError(failure, { idPrefix: 'applicant' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Change application status"
      size="sm"
      id="applicant-status-modal"
      autogenerate={{
        onGenerate: () => {
          const others = PICKABLE_APPLICANT_STATUS_OPTIONS.filter((o) => o.value !== applicant.status);
          setStatus(others[Math.floor(Math.random() * others.length)].value);
        },
        disabled: saving,
      }}
      confirm={{ id: 'applicant-status-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="applicant-status-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="applicant-status-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id="applicant-status"
          label="Status"
          required
          description={`Currently ${applicant.statusLabel}. Qualified and Not Qualified are set by the initial screening.`}
          placeholder="Choose a stage…"
          value={status === null ? null : String(status)}
          onChange={(value) => setStatus(value === '' ? null : (Number(value) as ApplicantStatusValue))}
          options={PICKABLE_APPLICANT_STATUS_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))}
          searchable={false}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
